import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import {
  BundleInterface,
  EduContentInterface,
  LevelInterface,
  MixedResultType,
  PersonInterface,
  Result,
  ResultInterface,
  ResultStatusEnum,
  SpreadInterface,
  StudentInterface,
  TaskInstanceInterface,
  TaskInterface,
} from '@campus/dal';
import { ContentActionInterface } from '@campus/shared';
import { HumanDateTimeFormat, HumanDateTimePipe } from '@campus/ui';
import { ListItemHeroType } from '@diekeure/dcr-components';
import '@diekeure/dcr-components/dcr-animated-kai.js';
import '@diekeure/dcr-components/dcr-avatar.js';
import '@diekeure/dcr-components/dcr-badge.js';
import '@diekeure/dcr-components/dcr-icon.js';
import '@diekeure/dcr-components/dcr-inline-icon-button.js';
import '@diekeure/dcr-components/dcr-list-item.js';
import '@diekeure/dcr-components/dcr-menu-item.js';
import '@diekeure/dcr-components/dcr-menu.js';
import '@diekeure/dcr-components/dcr-plain-tooltip.js';
import '@diekeure/dcr-components/dcr-tonal-button.js';
import { MixedResultWithActionsInterface } from '../../interfaces';

const EDUCONTENT_TYPE_DICT = {
  exercise: 'digitale oefening',
  drill: 'boost',
};

interface TotalInterface {
  person?: StudentInterface;
  eduContent?: EduContentInterface;
  type?: 'session' | 'sessionDay';
  results?: Result[];
  scorableResults?: Result[];
  attemptedResults?: Result[];
  attemptedAmount?: number;
  studentAmount?: number;
  taskAmount?: number;
  bundleAmount?: number;
  sumOfTime?: number;

  task?: TaskInterface;
  bundle?: BundleInterface;
  taskInstance?: TaskInstanceInterface;
  averageScore?: number;
  scorableEduContentAmount?: number;
  maxAmountScores?: number;
  assignees?: { classGroups: string[]; groups: string[]; students: string[]; studentCount: number };
  start?: Date;
  end?: Date;

  //Results V2
  data?: { attemptedAmount?: number; sumOfTime?: number };
  spread?: SpreadInterface;
  title?: string;
  subtitle?: string;
}

type ResultListItemViewType = 'person' | 'educontent' | 'goal' | 'task' | 'bundle';

let rliUniqueId = 0;

@Component({
  selector: 'campus-result-list-item',
  templateUrl: './result-list-item.component.html',
  styleUrls: ['./result-list-item.component.scss'],
  standalone: false,
})
export class ResultListItemComponent implements OnChanges, OnInit {
  @Input() type: 'listitem' | 'section' = 'listitem';
  @Input() view: ResultListItemViewType;
  @Input() total: TotalInterface;
  @Input() result: MixedResultWithActionsInterface<MixedResultType>;
  @Input() open: boolean;

  @Output() opened: EventEmitter<Event> = new EventEmitter<Event>();
  @Output() closed: EventEmitter<Event> = new EventEmitter<Event>();

  public person: PersonInterface;
  public eduContent: EduContentInterface;

  public results: ResultInterface[];
  public resultsMaxAmount: number;

  // what this item is about
  public headline: string;

  // hero-slot
  // showing scores in all ways
  public heroType: ListItemHeroType;

  public heroView: 'score' | 'scatterV2' | 'scatter' | 'ability' | 'avatar' | 'session' | 'sessionDay' | 'none';

  // supporting text
  // showing a date-range, a completed date, or attempt timestamp
  public date: Date;
  public dateStart: Date;
  public dateEnd: Date;
  public dateFormat: HumanDateTimeFormat = 'longDateTime';

  // trailing slot
  // showing more info about the result
  public id: number;
  public practicesAmount: number;
  public studentAmount: number;
  public eduContentAmount: number;
  public interactionAmount: number;
  public timeSpent: number;
  public inBundle: boolean;
  public inTask: boolean;
  public taskAmount: number;
  public bundleAmount: number;
  public level: LevelInterface;
  public eduContentType: { icon: string; label: string };
  public isSourceKai: boolean;
  public source: { id: string; icon: string; label: string; tooltip?: string };
  public cappedAssignees: { label: string }[];

  // actions-slot
  public actions: ContentActionInterface[];

  public ngOnInit(): void {
    this.id = rliUniqueId++;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.total && this.total) {
      this._resetProperties();
      this._setTotalView(this.total, this.view);
    }
    if (changes.result && this.result) {
      this._resetProperties();
      this.result = this._sanitizeResult(this.result);
      if (this.view === 'educontent' || this.view === 'person') {
        this._setResultEduContentView(this.result);
      } else if (this.view === 'goal') {
        this._setResultGoalView(this.result);
      }
    }
  }

  public onOpenChange(event: Event) {
    this.opened.emit(event);
  }
  public onCloseChange(event: Event) {
    this.closed.emit(event);
  }

  private _sanitizeResult(
    result: MixedResultWithActionsInterface<MixedResultType>
  ): MixedResultWithActionsInterface<MixedResultType> {
    if (result.data) return result;

    return {
      ...result,
      ...Result.toMixedResult(result),
    } as MixedResultWithActionsInterface<MixedResultType>;
  }

  private _setTotalView(total: TotalInterface, view: ResultListItemViewType) {
    const asPerson = view === 'person';
    const asTask = view === 'task';
    const asBundle = view === 'bundle';
    const asEduContent = view === 'educontent';

    if (this.total) {
      if (asPerson) {
        this.person = asPerson && total.person;
        this.practicesAmount = total.attemptedAmount || total?.data?.attemptedAmount;
        this.timeSpent = total.sumOfTime || total?.data?.sumOfTime;
        this.headline = total.person?.displayName || total?.title;
      }
      if (asTask) {
        this.headline = total.task.name;
      }
      if (asBundle) {
        this.headline = total.bundle.name;
      }
      if (asTask || asBundle) {
        this.eduContentAmount = total.scorableEduContentAmount;
        this.studentAmount = total.assignees?.studentCount;
        this.cappedAssignees = [
          ...(total.assignees?.classGroups || []),
          ...(total.assignees?.groups || []),
          ...(total.assignees?.students || []),
        ].map((label) => ({ label }));
        this.dateFormat = 'shortDate';
      }

      if (asEduContent) {
        this.headline = total.eduContent.publishedEduContentMetadata?.title;
        this.practicesAmount = total.results?.filter(
          ({ status }) => status !== ResultStatusEnum.STATUS_NOT_ATTEMPTED
        ).length;
        this.studentAmount = total.studentAmount;
        this.taskAmount = total.taskAmount;
        this.bundleAmount = total.bundleAmount;
      }

      this.dateStart = total.start;
      this.dateEnd = total.end;

      this.results = total.scorableResults ?? total.results;
      this.resultsMaxAmount = total.maxAmountScores ?? total.scorableResults?.length;

      this.heroType = 'score';
      this.heroView = total?.type === 'sessionDay' ? 'sessionDay' : total?.data ? 'scatterV2' : 'scatter';
    }
  }

  private _setResultEduContentView(result: MixedResultWithActionsInterface<MixedResultType>) {
    const asPerson = this.view === 'person';
    const asEduContent = this.view === 'educontent';

    if (asEduContent) {
      this.headline = result.eduContent.publishedEduContentMetadata?.title;
    } else if (asPerson) {
      this.headline = result.person.displayName;
    }
    this.eduContent = result.eduContent;
    this.date = result.lastUpdated;
    this.timeSpent = result.time;
    this.inBundle = result.inBundle;
    this.inTask = result.inTask;
    this.actions = result.actions;
    this.level = this.eduContent?.level;
    this.eduContentType = this._getType(this.eduContent);

    this.source = this._getSource(result);

    this.heroType = 'score';
    this.heroView = result.type === 'sessionDay' ? 'sessionDay' : result.type === 'session' ? 'session' : 'score';
  }

  private _setResultGoalView(result: MixedResultWithActionsInterface<MixedResultType>) {
    this.headline = result.goal.name;
    this.date = result.lastUpdated;
    this.timeSpent = result.time;
    this.interactionAmount = result.interactions;
    this.actions = result.actions;

    this.heroType = 'score';
    this.heroView = 'ability';
  }

  private _getSource(result: MixedResultWithActionsInterface<MixedResultType>): {
    id: string;
    icon: string;
    label: string;
    tooltip: string;
  } {
    if (!result) return;

    const data = result.data as ResultInterface;
    if (data?.recommendation || data?.loop)
      return { id: 'kai', icon: 'kai', label: '', tooltip: 'aangereikt door Kai' };

    if (result.inBundle)
      return {
        id: 'bundle',
        icon: 'bundle',
        label: 'Deze oefening zit in een bundel.',
        tooltip: 'zit in een bundel',
      };

    if (result.inTask)
      return {
        id: 'task',
        icon: 'task',
        label: 'Deze oefening zit in een taak.',
        tooltip: 'zit in een taak',
      };

    if (data?.unlockedFreePracticeId)
      return {
        id: 'digital-book',
        icon: 'digital-book',
        label: 'Deze oefening werd gemaakt in een digitaal boek.',
        tooltip: 'gemaakt in een digitaal boek',
      };

    return {
      id: 'ufp',
      icon: 'ufp',
      label: 'Deze oefening werd gemaakt bij zelfstandig leren.',
      tooltip: 'gemaakt bij zelf leren',
    };
  }

  private _getType(eduContent: EduContentInterface): { icon: string; label: string } {
    const icon = eduContent?.publishedEduContentMetadata?.fileExt || eduContent?.type;
    return { icon, label: EDUCONTENT_TYPE_DICT[eduContent?.type] ?? eduContent?.type };
  }

  private _resetProperties() {
    this.person = undefined;
    this.practicesAmount = undefined;
    this.timeSpent = undefined;
    this.headline = undefined;
    this.headline = undefined;
    this.results = undefined;
    this.resultsMaxAmount = undefined;
    this.heroType = undefined;
    this.heroView = undefined;
    this.cappedAssignees = undefined;
    this.dateFormat = 'longDateTime';
    this.date = undefined;
    this.dateStart = undefined;
    this.dateEnd = undefined;
    this.inBundle = undefined;
    this.inTask = undefined;
    this.bundleAmount = undefined;
    this.taskAmount = undefined;
    this.actions = undefined;
    this.level = undefined;
    this.eduContentType = undefined;
    this.interactionAmount = undefined;
    this.source = undefined;
  }

  private _humanDateTimePipe = new HumanDateTimePipe();

  // TODO REDO

  // @Input() person: PersonInterface;
  // @Input() eduContent: EduContentInterface;
  // @Input() goal: GoalInterface;
  // @Input() result: MixedResultInterface<MixedResultType>;
  // @Input() results: ResultInterface[];
  // @Input() maxAmountScores: number;
  // @Input() task: TaskInterface;

  // @Input() date: string;

  // @Input() totalPractices: number;
  // @Input() totalStudents: number;
  // @Input() totalEduContent: number;
  // @Input() spentTime: number;
  // @Input() interactions: number;
  // @Input() inBundle: false;
  // @Input() inTask: false;

  // @Input() status: ResultStatusEnum;

  // @Input() actions: ContentActionInterface[];

  // public get isScatter() {
  //   return this.results && this.maxAmountScores;
  // }

  // public get heroView(): 'score' | 'scatter' | 'ability' | 'avatar' | 'none' {
  //   if (this.type === 'goal') {
  //     return this.result ? 'ability' : 'none';
  //   }
  //   if (this.isScatter) return 'scatter';
  //   if (this.result) return 'score';
  //   if (this.type === 'person' && this.person) {
  //     return 'avatar';
  //   }
  //   return 'none';
  // }

  public get supportingText() {
    if (this.date) return this._humanDateTimePipe.transform(this.date, { format: this.dateFormat });
    if (this.dateStart && this.dateEnd) {
      return (
        this._humanDateTimePipe.transform(this.dateStart, { format: this.dateFormat }) +
        ' - ' +
        this._humanDateTimePipe.transform(this.dateEnd, { format: this.dateFormat })
      );
    }
    return this.dateStart
      ? this._humanDateTimePipe.transform(this.dateStart, { format: this.dateFormat })
      : this.dateEnd
        ? this._humanDateTimePipe.transform(this.dateEnd, { format: this.dateFormat })
        : undefined;
  }
}
